import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte,
    BokkzAlgDataService,
    IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, combineLatest, Subject, takeUntil } from 'rxjs';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { NgIf } from '@angular/common';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-premieplicht',
    templateUrl: './premieplicht.component.html',
    styleUrls: ['./premieplicht.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiAlertModule,
        SharedUiButtonModule,
        SharedUiTooltipModule
    ]
})
export class PremieplichtComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    @Input() partner = new BehaviorSubject<boolean>(false);
    private destroy$ = new Subject<void>();
    public partnerBoolean = {} as boolean;
    public clientId!: string;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public formGroup = {} as FormGroup;
    public vraagAOWenANW = false;
    public vraagWLZenZVW = false;
    public vraagVermindering = false;
    public vraagCorrectie = false;
    public datumFouten: string[] = [];

    constructor(public dataService: BokkzIbDataService,
                public dataAlgService: BokkzAlgDataService,
                private fb: FormBuilder) {
        this.formGroup = this.fb.group({
            periodeNietVerzekerdVoorAOWenANW: this.fb.group({
                begindatum: null as unknown as Date,
                einddatum: null as unknown as Date
            }),
            periodeNietVerzekerdVoorWLZenWVZ: this.fb.group({
                begindatum: null as unknown as Date,
                einddatum: null as unknown as Date
            }),
            verminderingPremieInkomen: null as unknown as number,
            correctiePremieInkomen: null as unknown as number
        });
    }


    ngOnInit(): void {
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe(() => {
            combineLatest([
                this.dataService.incomeTaxEntity,
                this.partner
            ])
                .pipe(takeUntil(this.destroy$))
                .subscribe(([incomeTaxEntity, partner]) => {
                    this.incomeTaxEntity = incomeTaxEntity;
                    this.partnerBoolean = partner;
                    this.updateForm();
                });
        });
    }

    ngOnDestroy(): void {
        this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateForm() {
        this.formGroup.reset();
        this.vraagAOWenANW = false;
        this.vraagWLZenZVW = false;
        this.vraagVermindering = false;
        this.vraagCorrectie = false;

        const premieplicht = !this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.premieplicht
            : this.incomeTaxEntity.aangifte?.partnerAangifte?.premieplicht;

        if (!premieplicht) return;

        this.formGroup.patchValue({
            periodeNietVerzekerdVoorAOWenANW: {
                begindatum: premieplicht.periodeNietVerzekerdVoorAOWenANW?.begindatum
                    ? new Date(premieplicht.periodeNietVerzekerdVoorAOWenANW.begindatum)
                    : null,
                einddatum: premieplicht.periodeNietVerzekerdVoorAOWenANW?.einddatum
                    ? new Date(premieplicht.periodeNietVerzekerdVoorAOWenANW.einddatum)
                    : null
            },
            periodeNietVerzekerdVoorWLZenWVZ: {
                begindatum: premieplicht.periodeNietVerzekerdVoorWLZenWVZ?.begindatum
                    ? new Date(premieplicht.periodeNietVerzekerdVoorWLZenWVZ.begindatum)
                    : null,
                einddatum: premieplicht.periodeNietVerzekerdVoorWLZenWVZ?.einddatum
                    ? new Date(premieplicht.periodeNietVerzekerdVoorWLZenWVZ.einddatum)
                    : null
            },
            verminderingPremieInkomen: premieplicht.verminderingPremieInkomen !== undefined ? premieplicht.verminderingPremieInkomen : null,
            correctiePremieInkomen: premieplicht.correctiePremieInkomen !== undefined ? premieplicht.correctiePremieInkomen : null
        }, { emitEvent: false });

        const begindatum = premieplicht.periodeNietVerzekerdVoorAOWenANW?.begindatum;
        const einddatum = premieplicht.periodeNietVerzekerdVoorAOWenANW?.einddatum;
        this.vraagAOWenANW = !!(begindatum || einddatum);

        const begindatum2 = premieplicht.periodeNietVerzekerdVoorWLZenWVZ?.begindatum;
        const einddatum2 = premieplicht.periodeNietVerzekerdVoorWLZenWVZ?.einddatum;
        this.vraagWLZenZVW = !!(begindatum2 || einddatum2);

        const vermindering = premieplicht.verminderingPremieInkomen;
        this.vraagVermindering= !!(vermindering);

        const correctie = premieplicht.correctiePremieInkomen;
        this.vraagCorrectie = !!(correctie);
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity();
        setTimeout(() => {
            const raw = this.formGroup.getRawValue();
            // Bepaal welke secties zichtbaar zijn
            this.vraagAOWenANW = !!(raw.periodeNietVerzekerdVoorAOWenANW?.begindatum || raw.periodeNietVerzekerdVoorAOWenANW?.einddatum);
            this.vraagWLZenZVW = !!(raw.periodeNietVerzekerdVoorWLZenWVZ?.begindatum || raw.periodeNietVerzekerdVoorWLZenWVZ?.einddatum);
            this.vraagVermindering = !!(raw.verminderingPremieInkomen);
            this.vraagCorrectie = !!(raw.correctiePremieInkomen);

            //Leegmaken als de sectie verborgen is
            if (!this.vraagAOWenANW) {
                this.formGroup.get('periodeNietVerzekerdVoorAOWenANW')?.reset({ begindatum: null, einddatum: null }, { emitEvent: false });
            }
            if (!this.vraagWLZenZVW) {
                this.formGroup.get('periodeNietVerzekerdVoorWLZenWVZ')?.reset({ begindatum: null, einddatum: null }, { emitEvent: false });
            }
            if (!this.vraagVermindering) {
                this.formGroup.get('verminderingPremieInkomen')?.reset(null, { emitEvent: false });
            }
            if (!this.vraagCorrectie) {
                this.formGroup.get('correctiePremieInkomen')?.reset(null, { emitEvent: false });
            }
            if (!this.partnerBoolean) {
                this.incomeTaxEntity.aangifte = {
                    ...this.incomeTaxEntity.aangifte,
                    premieplicht: this.formGroup.getRawValue(),
                    buitenlandseBelastingplicht: {
                        ...this.incomeTaxEntity.aangifte?.buitenlandseBelastingplicht
                    }
                } as Aangifte;
            } else {
                this.incomeTaxEntity.aangifte = {
                    ...this.incomeTaxEntity.aangifte,
                    partnerAangifte: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                        premieplicht: this.formGroup.getRawValue(),
                        buitenlandseBelastingplicht: {
                            ...this.incomeTaxEntity.aangifte?.partnerAangifte?.buitenlandseBelastingplicht
                        }
                    }
                } as Aangifte;
            }
            this.valideerDatums();
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }, 10);
    }

    protected showAOWenANW() {
        this.vraagAOWenANW = true;
    }

    protected showWLZenZVW() {
        this.vraagWLZenZVW = true;
    }

    protected showVermindering() {
        this.vraagVermindering = true;
    }

    protected showCorrectie() {
        this.vraagCorrectie = true;
    }

    protected closeAOWenANW() {
        this.vraagAOWenANW = false;
        this.formGroup.get('periodeNietVerzekerdVoorAOWenANW')?.reset({ begindatum: null, einddatum: null }, { emitEvent: false });
        this.saveForm();
    }

    protected closeWLZenZVW() {
        this.vraagWLZenZVW = false;
        this.formGroup.get('periodeNietVerzekerdVoorWLZenWVZ')?.reset({ begindatum: null, einddatum: null }, { emitEvent: false });
        this.saveForm();
    }

    protected closeVermindering() {
        this.vraagVermindering = false;
        this.formGroup.get('verminderingPremieInkomen')?.reset(null, { emitEvent: false });
        this.saveForm();
    }

    protected closeCorrectie() {
        this.vraagCorrectie = false;
        this.formGroup.get('correctiePremieInkomen')?.reset(null, { emitEvent: false });
        this.saveForm();
    }

    private valideerDatums(): void {
        this.datumFouten = [];
        const jaar = this.incomeTaxEntity.aangifte?.aangifteJaar;

        if (!jaar) return;

        const start = new Date(jaar, 0, 1);   // 1 jan
        const eind  = new Date(jaar+1, 0, 1); // 31 dec

        const check = (datum: Date | null, label: string) => {
            if (!datum) return;
            const d = new Date(datum);
            if (d < start || d > eind) {
                this.datumFouten.push(`${label} (${d.toLocaleDateString('nl-NL')}) valt buiten het aangiftejaar ${jaar}.`);
            }
        };

        const raw = this.formGroup.getRawValue();
        check(raw.periodeNietVerzekerdVoorAOWenANW?.begindatum,  'Begindatum AOW/Anw');
        check(raw.periodeNietVerzekerdVoorAOWenANW?.einddatum,   'Einddatum AOW/Anw');
        check(raw.periodeNietVerzekerdVoorWLZenWVZ?.begindatum,  'Begindatum Wlz/Zvw');
        check(raw.periodeNietVerzekerdVoorWLZenWVZ?.einddatum,   'Einddatum Wlz/Zvw');
    }


}
